import React from "react";
import * as motion from "motion/react-client";
import {
  appearanceAnimation,
  bounceAnimation,
} from "../../../utils/animations";

import chinaFlag from "../../../assets/home/china-flag.png";
import jpFlag from "../../../assets/home/jp-flag.png";
import ausFlag from "../../../assets/home/aus-flag.png";
import usFlag from "../../../assets/home/us-flag.png";
import { ArrowRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

interface PopularCountryItem {
  flag: string;
  name: string;
  content: string[];
}

const PopularCountriesSection = () => {
  const { t } = useTranslation();

  const popularCountries: PopularCountryItem[] = [
    {
      flag: usFlag,
      name: t("home.popular_countries.usa"),
      content: [
        t("home.popular_countries.usa.line1"),
        t("home.popular_countries.usa.line2"),
        t("home.popular_countries.usa.line3"),
      ],
    },
    {
      flag: jpFlag,
      name: t("home.popular_countries.japan"),
      content: [
        t("home.popular_countries.japan.line1"),
        t("home.popular_countries.japan.line2"),
        t("home.popular_countries.japan.line3"),
      ],
    },
    {
      flag: ausFlag,
      name: t("home.popular_countries.australia"),
      content: [
        t("home.popular_countries.australia.line1"),
        t("home.popular_countries.australia.line2"),
        t("home.popular_countries.australia.line3"),
      ],
    },
    {
      flag: chinaFlag,
      name: t("home.popular_countries.china"),
      content: [
        t("home.popular_countries.china.line1"),
        t("home.popular_countries.china.line2"),
        t("home.popular_countries.china.line3"),
      ],
    },
  ];

  return (
    <div className="w-full flex justify-center items-center px-6 py-20">
      <div className="w-full max-w-[1290px] flex flex-col gap-28 justify-center items-center">
        <div className="w-full flex max-lg:flex-col max-lg:gap-6">
          <motion.div
            {...appearanceAnimation("bottom")}
            className="lg:w-4/5 flex justify-center items-center"
          >
            {/* Title */}
            <div className="lg:w-[50%] flex flex-col justify-center items-start gap-6">
              <p className="text-[#074799] text-sm font-semibold">
                {t("home.popular_countries")}
              </p>
              <p className="text-[#074799] md:text-5xl text-3xl font-bold leading-tight">
                {t("home.popular_countries.title")}
              </p>
            </div>
          </motion.div>

          <div className="lg:w-1/5 flex lg:justify-center justify-end items-center">
            <button
              type="button"
              className="max-lg:text-sm bg-[#F4E920] hover:text-white text-[#074799] hover:bg-primary-blue duration-300 font-semibold px-8 lg:py-6 py-4 rounded-full flex justify-center items-center gap-2"
            >
              {t("cta.view_more")}
              <ArrowRight size={20} />
            </button>
          </div>
        </div>

        <div className="w-full grid xl:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-8">
          {popularCountries.map((country, index) => (
            <CountryCard key={index} country={country} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PopularCountriesSection;

interface CountryCardProps {
  country: PopularCountryItem;
  index: number;
}

const CountryCard: React.FC<CountryCardProps> = ({ country, index }) => {
  return (
    <motion.div
      {...bounceAnimation(index)}
      className="border px-6 py-8 flex flex-col gap-5 rounded-2xl w-full justify-center items-start bg-white text-center cursor-pointer hover:shadow-xl duration-300"
    >
      <div className="border border-[#F4E920] rounded-full flex items-center justify-center">
        <img src={country.flag} alt={country.name} />
      </div>

      <p className="text-[#074799] font-bold text-[22px]">{country.name}</p>

      <ul className="text-[#074799] space-y-2">
        {country.content.map((item, index) => (
          <li key={index} className="text-left">
            <Check size={16} className="inline-block mr-3" />
            {item}
          </li>
        ))}
      </ul>
    </motion.div>
  );
};
